import React, { useState, useEffect, useMemo } from "react";
import Helmet from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import {
  roleListApi,
  deleteRole,
  statusChangeRoleApi,
  updateRoleApi,
  getActivePermissionList,
} from "store/actions";
import Table from "components/Table";
import Columns from "./Columns";
import FormikForm from "components/shared/FormikForm";
import AddUpdateRole from "components/Modals/AddUpdateRole";
import ViewRoleModal from "components/Modals/viewRole";
import PermissionModal from "components/Modals/permissionModel";
import PermissionGate from "components/PermissionGate";
import usePermission from "hooks/usePermission";

const InputFieldsForSearch = [
  {
    name: "search",
    type: "text",
    label: "",
    placeholder: "Search...",
  },
  {
    name: "status",
    type: "select",
    label: "",
    placeholder: "Select status",
    options: [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
  },
];

const initialValuesForFilter = {
  search: "",
  status: "",
};

const List = () => {
  const dispatch = useDispatch();

  const { RoleList, totalRole, loading } = useSelector((state) => state.Permission);

  const [columns, setcolumns] = useState([{ dataField: "", text: "" }]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  const [showAddUpdatePop, setShowAddUpdatePop] = useState({
    show: false,
    rowData: null,
  });
  const [viewRoleId, setViewRoleId] = useState(null);

  const [filter, setFilter] = useState({
    page: 1,
    limit: 20,
    order: -1,
    orderBy: "createdAt",
    search: "",
    status: "",
  });

  const { hasPermission } = usePermission();
  const permissions = useMemo(() => ({
    add: hasPermission("add_role"),
    edit: hasPermission("edit_role"),
    delete: hasPermission("delete_role"),
  }), [hasPermission]);


  const handleRoleStatus = (row, status) => {
    try {
      const callBack = (err, res) => {
        if (err) return;
        getApiData(filter);
      };

      const payload = {
        _id: row?._id,
        status,
      };

      dispatch(
        statusChangeRoleApi({
          data: payload,
          callBack,
        }),
      );
    } catch (error) {
      console.error(error, "<===err");
    }
  };

  const handlePermissionHandler = (row) => {
    setSelectedRole(row);
    setShowPermissionModal(true);
  };

  useEffect(() => {
    getApiData(filter);
  }, [filter]);

  useEffect(() => {
    setcolumns(
      Columns({
        handleEditHandler,
        handleRoleStatus,
        deleteHandler,
        handleViewHandler,
        handlePermissionHandler,
        permissions,
      })?.filter((item) => {
        if (
          item?.text === "Action" &&
          !permissions.edit &&
          !permissions?.delete
        ) {
          return false;
        }
        return true;
      }),
    );
  }, [permissions.edit, permissions.delete]);


  const fetchAllPermissions = (action) => {
    setPermissionsLoading(true);
    dispatch(
      getActivePermissionList({
        data: {
          page: 1,
          limit: 1000,
          ...(action && action !== "all" ? { action } : {}),
        },
        callBack: (err, res) => {
          setPermissionsLoading(false);
          if (err) return;
          setAllPermissions(res?.data || res?.list || res || []);
        },
      }),
    );
  };

  useEffect(() => {
    fetchAllPermissions("all");
  }, []);

  async function getApiData(query) {
    try {
      dispatch(roleListApi({ data: query }));
    } catch (error) { }
  }

  const handleFilterForm = (info) => {
    setFilter({
      ...filter,
      page: 1,
      limit: 20,
      order: -1,
      orderBy: "createdAt",
      search: info.search,
      status: info.status,
    });
  };

  const handleEditHandler = (info) => {
    setShowAddUpdatePop({
      show: true,
      rowData: info,
    });
  };

  const deleteHandler = (id) => {
    try {
      const userConfirmed = window.confirm(
        "Are you sure, you want to delete this role?",
      );
      if (userConfirmed) {
        const callBack = (err, res) => {
          if (err) {
          } else {
            getApiData(filter);
          }
        };

        const payload = {
          _id: id,
          status: "deleted",
        };

        dispatch(
          deleteRole({
            data: payload,
            callBack,
          }),
        );
      }
    } catch (error) {
      console.error(error, "<===err");
    }
  };

  const handleViewHandler = (row) => {
    setViewRoleId(row._id);
  };

  const closePermissionModal = () => {
    setShowPermissionModal(false);
    setSelectedRole(null);
  };

  const handleSavePermissions = (roleId, permissionKeys) => {
    return new Promise((resolve, reject) => {
      const payload = {
        _id: roleId,
        permissions: permissionKeys,
      };
      dispatch(
        updateRoleApi({
          data: payload,
          callBack: (err, response) => {
            if (err) {
              reject(err);
              return;
            }
            getApiData(filter); // refresh table so the tooltip/permissions column reflects the change
            resolve(response);
          },
        }),
      );
    });
  };

  const handleConfirmAddUpdate = () => {
    setShowAddUpdatePop({ show: false, rowData: null });
    getApiData(filter);
  };

  return (
    <>
      {loading && (
        <div className="main-loader">
          <FadeLoader size={1000} />
        </div>
      )}
      <Helmet title={"Role"} />
      {showAddUpdatePop.show && (
        <AddUpdateRole
          show={showAddUpdatePop.show}
          title={showAddUpdatePop?.rowData ? "Update Role" : "Add Role"}
          loading={loading}
          handleClose={() =>
            setShowAddUpdatePop({ show: false, rowData: null })
          }
          handleConfirm={handleConfirmAddUpdate}
          rowData={showAddUpdatePop?.rowData}
        />
      )}

      {viewRoleId && (
        <ViewRoleModal
          show={!!viewRoleId}
          id={viewRoleId}
          handleClose={() => setViewRoleId(null)}
        />
      )}

      {showPermissionModal && (
        <PermissionModal
          show={showPermissionModal}
          onHide={closePermissionModal}
          role={selectedRole}
          allPermissions={allPermissions}
          permissionsLoading={permissionsLoading}
          onSave={handleSavePermissions}
          onFilterChange={fetchAllPermissions}
        />
      )}

      <section className="section">
        <div className="section-header">
          <h1>Role</h1>
        </div>
        <div className="section-body">
          <div className="card">
            <div className="row justify-content-between">
              <div className="col-6">
                <div className="card-header">
                  <h2 className="section-title">Role List</h2>
                </div>
              </div>
              <PermissionGate permission="add_role">
                <div className="col-6">
                  <div className="card-header d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-primary"
                      onClick={() =>
                        setShowAddUpdatePop({
                          show: true,
                          rowData: null,
                        })
                      }
                    >
                      Add Role
                    </button>
                  </div>
                </div>
              </PermissionGate>
              <div className="col-12">
                <div className="card-header">
                  <FormikForm
                    initialValues={initialValuesForFilter}
                    onSubmit={handleFilterForm}
                    loading={loading}
                    inputFields={InputFieldsForSearch}
                    buttonText={"Search"}
                  />
                </div>
              </div>
            </div>
          </div>
          {RoleList?.length > 0 && (
            <Table
              data={RoleList}
              columns={columns}
              setFilter={setFilter}
              total={totalRole}
              filter={filter}
            />
          )}
          {!loading && RoleList?.length === 0 && (
            <p style={{ textAlign: "center" }}>No Role Found</p>
          )}
        </div>
      </section>
    </>
  );
};

export default List;